import React from 'react';
import { createStackNavigator } from '@react-navigation/stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { useTheme } from 'react-native-paper';
import { screenOptions } from './NavigationUtils';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

// Admin Dashboard Screen
import AdminDashboardScreen from '../screens/admin/AdminDashboardScreen';

// Admin Menu Screens - Menü yönetimi
import AdminMenuScreen from '../screens/admin/menu/AdminMenuScreen';
import CategoryListScreen from '../screens/admin/menu/CategoryListScreen';
import CategoryFormScreen from '../screens/admin/menu/CategoryFormScreen';
import MenuItemListScreen from '../screens/admin/menu/MenuItemListScreen';
import MenuItemFormScreen from '../screens/admin/menu/MenuItemFormScreen';

// Admin User Screens - Kullanıcı yönetimi
import UserListScreen from '../screens/admin/users/UserListScreen';
import UserDetailScreen from '../screens/admin/users/UserDetailScreen';
import UserFormScreen from '../screens/admin/users/UserFormScreen';

// Admin Children Screens - Çocuk yönetimi
import ChildrenListScreen from '../screens/admin/children/ChildrenListScreen';
import ChildDetailScreen from '../screens/admin/children/ChildDetailScreen';

// Admin Park Visits Screens - Oyun parkı ziyaretleri
import ActiveVisitsScreen from '../screens/admin/parkVisits/ActiveVisitsScreen';
import PendingVisitsScreen from '../screens/admin/parkVisits/PendingVisitsScreen';
import VisitHistoryScreen from '../screens/admin/parkVisits/VisitHistoryScreen';
import VisitsHubScreen from '../screens/admin/parkVisits/VisitsHubScreen';

// Admin Profile Screens - Profil yönetimi
import AdminProfileScreen from '../screens/admin/profile/AdminProfileScreen';
import EditProfileScreen from '../screens/admin/profile/EditProfileScreen';
import ChangePasswordScreen from '../screens/admin/profile/ChangePasswordScreen';
import NotificationSettingsScreen from '../screens/admin/profile/NotificationSettingsScreen';
import ParkSettingsScreen from '../screens/admin/profile/ParkSettingsScreen';

const Tab = createBottomTabNavigator();
const AdminStack = createStackNavigator();
const MenuStack = createStackNavigator();
const UsersStack = createStackNavigator();
const ChildrenStack = createStackNavigator();
const VisitsStack = createStackNavigator();
const ProfileStack = createStackNavigator();

// Menü Stack Navigator
const MenuStackNavigator = () => {
  return (
    <MenuStack.Navigator screenOptions={screenOptions}>
      <MenuStack.Screen 
        name="AdminMenu" 
        component={AdminMenuScreen} 
        options={{ title: 'Menü' }}
      />
      <MenuStack.Screen 
        name="Categories" 
        component={CategoryListScreen} 
        options={{ title: 'Kategoriler' }}
      />
      <MenuStack.Screen 
        name="CategoryForm" 
        component={CategoryFormScreen} 
        options={({ route }) => ({ 
          title: route.params?.category ? 'Kategori Düzenle' : 'Yeni Kategori' 
        })}
      />
      <MenuStack.Screen 
        name="MenuItems" 
        component={MenuItemListScreen} 
        options={({ route }) => ({ 
          title: route.params?.categoryName ? `${route.params.categoryName} - Ürünler` : 'Menü Ürünleri' 
        })}
      />
      <MenuStack.Screen 
        name="MenuItemForm" 
        component={MenuItemFormScreen} 
        options={({ route }) => ({ 
          title: route.params?.item ? 'Ürün Düzenle' : 'Yeni Ürün' 
        })}
      />
    </MenuStack.Navigator>
  );
};

// Users Stack Navigator
const UsersStackNavigator = () => {
  return (
    <UsersStack.Navigator screenOptions={screenOptions}>
      <UsersStack.Screen 
        name="UsersList" 
        component={UserListScreen} 
        options={{ title: 'Kullanıcılar' }}
      />
      <UsersStack.Screen 
        name="UserDetail" 
        component={UserDetailScreen} 
        options={({ route }) => ({ 
          title: route.params?.username ? route.params.username : 'Kullanıcı Detayı' 
        })}
      />
      <UsersStack.Screen 
        name="UserForm" 
        component={UserFormScreen} 
        options={({ route }) => ({ 
          title: route.params?.user ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı' 
        })}
      />
    </UsersStack.Navigator>
  );
};

// Children Stack Navigator
const ChildrenStackNavigator = () => {
  return (
    <ChildrenStack.Navigator screenOptions={screenOptions}>
      <ChildrenStack.Screen 
        name="ChildrenList" 
        component={ChildrenListScreen} 
        options={{ title: 'Çocuklar' }}
      />
      <ChildrenStack.Screen 
        name="ChildDetail" 
        component={ChildDetailScreen} 
        options={({ route }) => ({ 
          title: route.params?.childName ? route.params.childName : 'Çocuk Detayı' 
        })}
      />
      <ChildrenStack.Screen 
        name="VisitHistory" 
        component={VisitHistoryScreen} 
        options={({ route }) => ({ 
          title: route.params?.childName ? `${route.params.childName} - Ziyaret Geçmişi` : 'Ziyaret Geçmişi' 
        })}
      />
    </ChildrenStack.Navigator>
  );
};

// Visits Stack Navigator
const VisitsStackNavigator = () => {
  return (
    <VisitsStack.Navigator 
      screenOptions={screenOptions}
      initialRouteName="VisitsHub"
    >
      <VisitsStack.Screen 
        name="VisitsHub" 
        component={VisitsHubScreen} 
        options={{ title: 'Ziyaretler' }}
      />
      <VisitsStack.Screen 
        name="ActiveVisits" 
        component={ActiveVisitsScreen} 
        options={{ title: 'Aktif Ziyaretler' }}
      />
      <VisitsStack.Screen 
        name="PendingVisits" 
        component={PendingVisitsScreen} 
        options={{ 
          title: 'Onay Bekleyen Ziyaretler',
          headerShown: false 
        }}
      />
      <VisitsStack.Screen 
        name="VisitHistory" 
        component={VisitHistoryScreen} 
        options={{ title: 'Ziyaret Geçmişi' }}
      />
    </VisitsStack.Navigator>
  );
};

// Profile Stack Navigator
const ProfileStackNavigator = () => {
  return (
    <ProfileStack.Navigator screenOptions={screenOptions}>
      <ProfileStack.Screen 
        name="Profile" 
        component={AdminProfileScreen} 
        options={{ title: 'Profilim' }}
      />
      <ProfileStack.Screen 
        name="EditProfile" 
        component={EditProfileScreen} 
        options={{ title: 'Profili Düzenle' }}
      />
      <ProfileStack.Screen 
        name="ChangePassword" 
        component={ChangePasswordScreen} 
        options={{ title: 'Şifre Değiştir' }}
      />
      <ProfileStack.Screen 
        name="NotificationSettings" 
        component={NotificationSettingsScreen} 
        options={{ title: 'Bildirim Ayarları' }}
      />
      <ProfileStack.Screen 
        name="ParkSettings" 
        component={ParkSettingsScreen} 
        options={{ title: 'Oyun Parkı Ayarları' }}
      />
    </ProfileStack.Navigator>
  );
};

// Ana Admin Navigator (Tab Navigator)
const AdminNavigator = () => {
  const theme = useTheme();
  
  return (
    <Tab.Navigator
      screenOptions={({ route }) => ({
        tabBarIcon: ({ focused, color, size }) => {
          let iconName;

          if (route.name === 'AdminDashboard') {
            iconName = 'view-dashboard';
          } else if (route.name === 'UsersManagement') {
            iconName = 'account-group';
          } else if (route.name === 'ChildrenManagement') {
            iconName = 'human-male-child';
          } else if (route.name === 'VisitsManagement') {
            iconName = 'calendar-clock';
          } else if (route.name === 'MenuManagement') {
            iconName = 'food-fork-drink';
          } else if (route.name === 'ProfileManagement') {
            iconName = 'account-cog';
          }

          return <MaterialCommunityIcons name={iconName} size={size} color={color} />;
        },
        tabBarActiveTintColor: '#e8a2f9',
        tabBarInactiveTintColor: 'gray',
        headerShown: false,
      })}
    >
      <Tab.Screen 
        name="AdminDashboard" 
        component={AdminDashboardScreen} 
        options={{ 
          title: 'Ana Sayfa'
        }} 
      />
      <Tab.Screen 
        name="UsersManagement" 
        component={UsersStackNavigator} 
        options={{ 
          title: 'Kullanıcılar'
        }} 
        listeners={({ navigation }) => ({
          tabPress: e => {
            // Tab'a her tıklandığında doğrudan kullanıcılar ana sayfasına git
            e.preventDefault();
            navigation.navigate('UsersManagement', {
              screen: 'UsersList'
            });
          },
        })}
      />
      <Tab.Screen 
        name="ChildrenManagement" 
        component={ChildrenStackNavigator} 
        options={{ 
          title: 'Çocuklar'
        }} 
        listeners={({ navigation }) => ({
          tabPress: e => {
            // Tab'a her tıklandığında doğrudan çocuklar ana sayfasına git
            e.preventDefault();
            navigation.navigate('ChildrenManagement', {
              screen: 'ChildrenList'
            });
          },
        })}
      />
      <Tab.Screen 
        name="VisitsManagement" 
        component={VisitsStackNavigator} 
        options={{ 
          title: 'Ziyaretler'
        }}
        listeners={({ navigation }) => ({
          tabPress: e => {
            // Tab'a her tıklandığında doğrudan ziyaretler merkezine git
            e.preventDefault();
            navigation.navigate('VisitsManagement', {
              screen: 'VisitsHub'
            });
          },
        })}
      />
      <Tab.Screen 
        name="MenuManagement" 
        component={MenuStackNavigator} 
        options={{ 
          title: 'Menü'
        }} 
        listeners={({ navigation }) => ({
          tabPress: e => {
            // Tab'a her tıklandığında doğrudan menü ana sayfasına git
            e.preventDefault();
            navigation.navigate('MenuManagement', {
              screen: 'AdminMenu'
            });
          },
        })}
      />
      <Tab.Screen 
        name="ProfileManagement" 
        component={ProfileStackNavigator} 
        options={{ 
          title: 'Profil'
        }} 
        listeners={({ navigation }) => ({
          tabPress: e => {
            // Tab'a her tıklandığında doğrudan profil sayfasına git
            e.preventDefault();
            navigation.navigate('ProfileManagement', {
              screen: 'Profile'
            });
          },
        })}
      />
    </Tab.Navigator>
  );
};

export default AdminNavigator;
